"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.synthesizedRowKeysOf = synthesizedRowKeysOf;
exports.admittedRowKeysOf = admittedRowKeysOf;
exports.canRecoverRowKey = canRecoverRowKey;
exports.recoveredKeyCellsOf = recoveredKeyCellsOf;
exports.unheldRowCellsOf = unheldRowCellsOf;
exports.admittedRowContentOf = admittedRowContentOf;
exports.proposedRowContentOf = proposedRowContentOf;
const consensus_types_1 = require("../../../../Consensus/consensus.types");
const package_data_adjudication_types_1 = require("../../../../PackageDataAdjudication/package-data-adjudication.types");
const adjudication_slice_1 = require("./adjudication-slice");
const adjudication_step_types_1 = require("./adjudication-step.types");
function synthesizedRowKeysOf(input) {
    const held = new Set(input.rows.map((row) => row.rowKey));
    const raisedAsRows = new Set(input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Row ? [value.anchor.rowKey] : [])));
    const keys = new Set();
    for (const value of input.contested) {
        const { rowKey } = value.anchor;
        if (value.anchor.kind === consensus_types_1.AnchorKind.Field && !held.has(rowKey) && !raisedAsRows.has(rowKey)) {
            keys.add(rowKey);
        }
    }
    return [...keys];
}
function admittedRowKeysOf(input, output) {
    const held = new Set(input.rows.map((row) => row.rowKey));
    return new Set(output.groundTruthDecisions.flatMap((decision) => (decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
        decision.existence === package_data_adjudication_types_1.RowExistence.Present &&
        !held.has(decision.anchor.rowKey)) ?
        [decision.anchor.rowKey]
        : []));
}
function canRecoverRowKey(sliceKey) {
    return (0, adjudication_slice_1.signalKeyColumnOf)(sliceKey) !== null;
}
function recoveredKeyCellsOf(input, output, rowKey) {
    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
    const column = (0, adjudication_slice_1.signalKeyColumnOf)(input.sliceKey);
    const identifies = (name) => column !== null && slice.rowKeyOf({ [column]: name }) === rowKey;
    const created = output.signalDecisions.find((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Create && decision.name !== undefined && identifies(decision.name))?.name;
    const proposed = proposedRowContentOf(input, rowKey);
    if (proposed !== null) {
        const cells = Object.fromEntries(slice.keyColumnNames().map((key) => [key, proposed[key] ?? null]));
        return column === null || created === undefined ? cells : { ...cells, [column]: created };
    }
    if (column === null) {
        return null;
    }
    const held = input.roster.find((signal) => identifies(signal.name));
    return { [column]: created ?? held?.name ?? rowKey };
}
function unheldRowCellsOf(input, output, rowKey) {
    const keyCells = recoveredKeyCellsOf(input, output, rowKey);
    if (keyCells !== null) {
        return keyCells;
    }
    const decoded = (0, adjudication_slice_1.adjudicationSliceOf)(input).rowCellsFromKey(rowKey);
    return decoded === null ? null : respelledAsSnapshot(input, decoded);
}
function respelledAsSnapshot(input, decoded) {
    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input);
    const cells = input.rows.flatMap((row) => Object.entries(row.values).flatMap(([column, value]) => (typeof value === 'string' ? [{ column, value }] : [])));
    const fallbacks = [...cells.map((cell) => cell.value), ...input.roster.map((signal) => signal.name)];
    return Object.fromEntries(Object.entries(decoded).map(([column, normalized]) => {
        if (normalized === '') {
            return [column, normalized];
        }
        const candidates = [...cells.flatMap((cell) => (cell.column === column ? [cell.value] : [])), ...fallbacks];
        const spelling = candidates.find((candidate) => slice.normalizeValue(column, candidate, decoded) === normalized);
        return [column, spelling ?? normalized];
    }));
}
function admittedRowContentOf(input, output, rowKey) {
    const keyCells = recoveredKeyCellsOf(input, output, rowKey);
    if (keyCells === null) {
        return null;
    }
    const ruled = Object.fromEntries(output.groundTruthDecisions.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && decision.anchor.rowKey === rowKey && (0, adjudication_step_types_1.isGroundTruthRuled)(decision) ?
        [[decision.anchor.column, decision.rawValue ?? null]]
        : []));
    const proposed = proposedRowContentOf(input, rowKey);
    if (proposed === null && Object.keys(ruled).length === 0) {
        return null;
    }
    return { ...(proposed ?? {}), ...keyCells, ...ruled };
}
function proposedRowContentOf(input, rowKey) {
    const proposed = input.contested.find((value) => value.anchor.kind === consensus_types_1.AnchorKind.Row && value.anchor.rowKey === rowKey)?.proposed;
    if (proposed === undefined || proposed.kind !== consensus_types_1.AnchorKind.Row) {
        return null;
    }
    const content = proposed.rowContentRaw;
    return typeof content === 'object' && content !== null && !Array.isArray(content) ? content : null;
}
//# sourceMappingURL=adjudication-admitted-rows.js.map